class ZentrobadProductGallery extends HTMLElement {
  connectedCallback() {
    this.data = this.parseData();
    this.mainImage = this.querySelector('[data-gallery-main]');
    this.mainLink = this.querySelector('[data-gallery-main-link]');
    this.wishlistButton = this.querySelector('[data-zentrobad-wishlist-toggle]');
    this.thumbs = this.querySelector('[data-gallery-thumbs]');
    this.prevButton = this.querySelector('[data-gallery-prev]');
    this.nextButton = this.querySelector('[data-gallery-next]');
    this.currentImages = [];
    this.currentIndex = 0;
    this.isLightboxOpen = false;

    this.prevButton?.addEventListener('click', () => this.select(this.currentIndex - 1));
    this.nextButton?.addEventListener('click', () => this.select(this.currentIndex + 1));
    this.mainLink?.addEventListener('click', this.handleMainImageClick);
    this.wishlistButton?.addEventListener('click', this.handleWishlistClick);
    document.addEventListener('change', this.handleOptionChange);
    document.addEventListener('input', this.handleOptionChange);
    document.addEventListener('variant:update', this.handleVariantUpdate);
    document.addEventListener('keydown', this.handleDocumentKeydown);
    document.addEventListener('shopify:section:load', this.handleOptionChange);
    this.observer = new MutationObserver(this.handleDomMutation);
    this.observer.observe(document.body, { childList: true, subtree: true });

    this.buildLightbox();
    this.updateWishlistButton();
    this.updateWishlistCounts();
    this.enhanceColorOptions();
    this.renderForVariant(this.data.selectedVariantId);
  }

  disconnectedCallback() {
    this.imageRequestId = (this.imageRequestId || 0) + 1;
    this.cancelImageLoad?.();
    document.removeEventListener('change', this.handleOptionChange);
    document.removeEventListener('input', this.handleOptionChange);
    document.removeEventListener('variant:update', this.handleVariantUpdate);
    document.removeEventListener('keydown', this.handleDocumentKeydown);
    document.removeEventListener('shopify:section:load', this.handleOptionChange);
    this.mainLink?.removeEventListener('click', this.handleMainImageClick);
    this.wishlistButton?.removeEventListener('click', this.handleWishlistClick);
    this.observer?.disconnect();
    this.lightbox?.remove();
    document.documentElement.classList.remove('zentrobad-lightbox-open');
  }

  parseData() {
    const script = this.querySelector('[data-zentrobad-gallery-data]');
    if (!script) return { variants: [], groups: [] };
    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.warn('Invalid Zentrobad gallery data', error);
      return { variants: [], groups: [] };
    }
  }

  handleOptionChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) && !(target instanceof HTMLSelectElement)) return;
    if (!target.closest('variant-picker')) return;

    this.renderFromCurrentSelection();
    window.setTimeout(() => this.enhanceColorOptions(), 0);
    window.setTimeout(() => this.renderFromCurrentSelection(), 80);
    window.setTimeout(() => this.enhanceColorOptions(), 250);
    window.setTimeout(() => this.renderFromCurrentSelection(), 300);
  };

  handleVariantUpdate = (event) => {
    const variantId = Number(event?.detail?.resource?.id || this.findSelectedVariantId());
    window.setTimeout(() => {
      if (variantId) {
        this.renderForVariant(variantId);
      } else {
        this.renderFromCurrentSelection();
      }
      this.enhanceColorOptions();
    }, 0);
  };

  handleDomMutation = () => {
    window.clearTimeout(this.mutationTimer);
    this.mutationTimer = window.setTimeout(() => this.enhanceColorOptions(), 60);
  };

  handleMainImageClick = (event) => {
    event.preventDefault();
    this.openLightbox(this.currentIndex);
  };

  handleWishlistClick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const product = this.getWishlistProduct();
    if (!product.id) return;

    const items = this.readWishlist();
    const existingIndex = items.findIndex((item) => String(item.id) === String(product.id));

    if (existingIndex >= 0) {
      items.splice(existingIndex, 1);
    } else {
      items.unshift(product);
    }

    this.writeWishlist(items);
    this.updateWishlistButton();
    this.updateWishlistCounts();
  };

  handleDocumentKeydown = (event) => {
    if (!this.isLightboxOpen) return;
    if (event.key === 'Escape') this.closeLightbox();
    if (event.key === 'ArrowLeft') this.select(this.currentIndex - 1);
    if (event.key === 'ArrowRight') this.select(this.currentIndex + 1);
  };

  enhanceColorOptions() {
    document.querySelectorAll('variant-picker fieldset.variant-option--buttons').forEach((fieldset) => {
      const legend = fieldset.querySelector('legend')?.textContent?.trim().toLowerCase() || '';
      // "Chromgriff" is a grip value, not a product color. Keep grip cards
      // separate from the color-swatch enhancement and its click handler.
      if (legend.includes('griff')) return;
      const hasColorValues = Array.from(fieldset.querySelectorAll('[data-key="variant-option-text"]')).some((text) => {
        return this.colorForValue(text.textContent);
      });
      if (!legend.includes('farbe') && !hasColorValues) return;

      fieldset.classList.add('zentrobad-color-options');
      fieldset.querySelectorAll('.variant-option__button-label').forEach((label) => {
        const text = label.querySelector('[data-key="variant-option-text"]')?.textContent?.trim() || '';
        if (!label.dataset.zentrobadSwatchBound) {
          label.addEventListener('click', this.handleSwatchLabelClick);
          label.dataset.zentrobadSwatchBound = 'true';
        }
        if (!text || label.querySelector('.zentrobad-color-dot')) return;
        const dot = document.createElement('span');
        dot.className = 'zentrobad-color-dot';
        dot.style.setProperty('--zentrobad-swatch-color', this.colorForValue(text) || '#f5f3ef');
        dot.setAttribute('aria-hidden', 'true');
        label.insertBefore(dot, label.querySelector('[data-key="variant-option-text"]'));
      });
    });
  }

  handleSwatchLabelClick = (event) => {
    const label = event.currentTarget;
    if (!(label instanceof HTMLLabelElement)) return;
    const input = label.querySelector('input[type="radio"]');
    if (!(input instanceof HTMLInputElement) || input.checked) return;

    input.checked = true;
    input.dataset.currentChecked = 'true';
    label.closest('fieldset')?.querySelectorAll('input[type="radio"]').forEach((radio) => {
      if (radio !== input) radio.dataset.currentChecked = 'false';
    });

    input.dispatchEvent(new Event('change', { bubbles: true }));
    window.setTimeout(() => this.renderFromCurrentSelection(), 0);
  };

  findSelectedVariantId() {
    const selectedVariant = this.findVariantBySelectedOptions();
    if (selectedVariant?.id) return Number(selectedVariant.id);

    const selectOption = document.querySelector('variant-picker select option:checked[data-variant-id]');
    if (selectOption?.dataset.variantId) return Number(selectOption.dataset.variantId);

    const checkedInput = document.querySelector('variant-picker input:checked[data-variant-id]');
    if (checkedInput?.dataset.variantId) return Number(checkedInput.dataset.variantId);

    return this.data.selectedVariantId;
  }

  renderFromCurrentSelection() {
    const selectedValue = this.findSelectedColorValue();
    const variant = this.findVariantBySelectedOptions() || this.data.variants?.find((item) => {
      return Number(item.id) === Number(this.findSelectedVariantId());
    });

    if (variant?.id) {
      this.renderForVariant(variant.id);
      return;
    }

    const group = this.findGroupByValue(selectedValue) || this.data.groups?.[0];
    const images = this.normalizeImages(group, null);
    this.currentImages = images;
    this.currentIndex = 0;
    this.renderThumbs();
    this.select(0);
  }

  findSelectedColorValue() {
    const colorFieldset = Array.from(document.querySelectorAll('variant-picker fieldset')).find((fieldset) => {
      const legend = fieldset.querySelector('legend')?.textContent || '';
      return this.normalize(legend).includes('farbe');
    });
    const checked = colorFieldset?.querySelector('input:checked');
    if (checked instanceof HTMLInputElement) return checked.value;

    const select = Array.from(document.querySelectorAll('variant-picker select')).find((element) => {
      return this.normalize(element.name).includes('farbe') || this.normalize(element.id).includes('farbe');
    });
    if (select instanceof HTMLSelectElement) return select.value;

    return '';
  }

  findVariantByOption(value) {
    const normalized = this.normalize(value);
    if (!normalized) return null;
    return this.data.variants?.find((variant) => {
      return [variant.option1, variant.option2, variant.option3, variant.title].some((option) => this.normalize(option) === normalized);
    }) || null;
  }

  findVariantBySelectedOptions() {
    const selectedOptions = this.getSelectedOptionValues();
    if (!selectedOptions.length) return null;
    return this.data.variants?.find((variant) => {
      return selectedOptions.every((value, index) => this.normalize(variant[`option${index + 1}`]) === this.normalize(value));
    }) || null;
  }

  getSelectedOptionValues() {
    const picker = document.querySelector('variant-picker');
    if (!picker) return [];
    const values = [];
    picker.querySelectorAll('fieldset.variant-option').forEach((fieldset) => {
      const checked = fieldset.querySelector('input:checked');
      if (checked instanceof HTMLInputElement) values[Number(checked.dataset.fieldsetIndex || values.length)] = checked.value;
    });
    picker.querySelectorAll('.variant-option--dropdowns select').forEach((select, index) => {
      if (select instanceof HTMLSelectElement) values[index] = select.value;
    });
    return values.filter((value) => value !== undefined && value !== '');
  }

  renderForVariant(variantId) {
    const variant = this.data.variants?.find((item) => Number(item.id) === Number(variantId)) || this.data.variants?.[0];
    const group = this.findGroup(variant);
    const images = this.normalizeImages(group, variant);

    this.currentImages = images;
    this.currentIndex = 0;
    this.renderThumbs();
    this.select(0);
  }

  findGroup(variant) {
    if (!variant) return this.data.groups?.[0];
    const title = this.normalize(variant.title);
    const optionKey = this.variantOptionKey(variant);
    return this.data.groups?.find((group) => {
      return this.normalize(group.variantTitle) === title || this.groupOptionKey(group) === optionKey;
    }) || null;
  }

  findGroupByValue(value) {
    const normalized = this.normalize(value);
    if (!normalized) return null;
    return this.data.groups?.find((group) => {
      return [group.option1, group.option2, group.option3, group.variantTitle].some((option) => this.normalize(option) === normalized);
    }) || null;
  }

  variantOptionKey(variant) {
    return [variant?.option1, variant?.option2, variant?.option3].map((value) => this.normalize(value)).join('|');
  }

  groupOptionKey(group) {
    return [group?.option1, group?.option2, group?.option3].map((value) => this.normalize(value)).join('|');
  }

  normalizeImages(group, variant) {
    const images = (group?.images || [])
      .filter((image) => image?.highRes || image?.lowRes)
      .map((image, index) => ({
        highRes: this.toContainImageUrl(image.highRes || image.lowRes, 1600),
        lowRes: image.lowRes || image.highRes,
        alt: image.title || `${this.data.productTitle || ''} ${group?.variantTitle || ''}`.trim(),
        index,
      }));

    if (!images.length && variant?.featuredImage) {
      images.push({
        highRes: this.toContainImageUrl(variant.featuredImage, 1600),
        lowRes: variant.featuredImage,
        alt: `${this.data.productTitle || ''} ${variant.title || ''}`.trim(),
        index: 0,
      });
    }
    return images;
  }

  renderThumbs() {
    if (!this.thumbs) return;
    this.thumbs.innerHTML = '';
    this.currentImages.forEach((image, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'zentrobad-gallery__thumb';
      button.setAttribute('aria-label', `Bild ${index + 1} anzeigen`);
      button.dataset.index = String(index);
      button.innerHTML = `<img src="${this.escapeAttribute(image.lowRes)}" alt="${this.escapeAttribute(image.alt)}" loading="lazy">`;
      button.addEventListener('click', () => this.select(index));
      this.thumbs.append(button);
    });
    this.renderLightboxThumbs();
  }

  select(index) {
    if (!this.currentImages.length || !this.mainImage) return this.loadImage(null);
    const nextIndex = (index + this.currentImages.length) % this.currentImages.length;
    const image = this.currentImages[nextIndex];
    this.currentIndex = nextIndex;
    this.updateWishlistButton();

    this.querySelectorAll('.zentrobad-gallery__thumb').forEach((thumb) => {
      thumb.toggleAttribute('aria-current', Number(thumb.dataset.index) === nextIndex);
    });

    this.updateLightbox();
    return this.loadImage(image);
  }

  setImageState(state) {
    const viewport = this.querySelector('.zentrobad-gallery__viewport');
    [viewport, this.lightbox].filter(Boolean).forEach((container) => {
      container.dataset.galleryImageState = state;
      container.setAttribute('aria-busy', String(state === 'loading'));
      const status = container.querySelector('[data-gallery-image-status]');
      if (!status) return;
      status.hidden = state === 'ready';
      status.textContent = state === 'loading' ? 'Bild wird geladen' : state === 'error' ? 'Bild derzeit nicht verfügbar' : '';
    });
  }

  loadImage(image) {
    const imageUrl = image?.highRes;
    const requestKey = `${imageUrl || ''}|${this.isLightboxOpen}`;
    // Variant events can repeat while Shopify refreshes its option controls.
    if (this.pendingImageKey === requestKey) return this.imageLoadPromise;
    const requestId = this.imageRequestId = (this.imageRequestId || 0) + 1;
    this.cancelImageLoad?.();
    this.pendingImageKey = null;
    this.cancelImageLoad = null;
    if (!imageUrl || !this.mainImage) {
      this.setImageState('error');
      return Promise.resolve(false);
    }

    const targets = [this.mainImage, this.isLightboxOpen ? this.lightboxImage : null].filter(Boolean);
    const alt = image.alt || this.data.productTitle || '';
    if (targets.every(target => target.src === imageUrl && target.complete && target.naturalWidth > 0)) {
      targets.forEach(target => { target.alt = alt; });
      if (this.mainLink) this.mainLink.href = imageUrl;
      this.setImageState('ready');
      return Promise.resolve(true);
    }

    this.setImageState('loading');
    this.pendingImageKey = requestKey;
    this.imageLoadPromise = new Promise(resolve => {
      const loader = new Image();
      let settled = false;
      let committing = false;
      const isCurrent = () => !settled && requestId === this.imageRequestId;
      const finish = result => {
        if (settled) return;
        settled = true;
        loader.onload = loader.onerror = null;
        if (requestId === this.imageRequestId) {
          this.pendingImageKey = null;
          this.cancelImageLoad = null;
        }
        resolve(result);
      };
      const fail = () => {
        if (!isCurrent()) return;
        this.setImageState('error');
        finish(false);
      };
      const showImage = async () => {
        if (!isCurrent() || committing) return;
        committing = true;
        try {
          if (loader.decode) await loader.decode();
          if (!isCurrent()) return;
          targets.forEach(target => {
            target.removeAttribute('srcset');
            target.removeAttribute('sizes');
            target.loading = 'eager';
            target.src = imageUrl;
            target.alt = alt;
          });
          if (this.mainLink) this.mainLink.href = imageUrl;
          await Promise.all(targets.map(target => target.decode ? target.decode() : Promise.resolve()));
          if (!isCurrent()) return;
          this.setImageState('ready');
          finish(true);
        } catch {
          fail();
        }
      };
      this.cancelImageLoad = () => finish(false);
      loader.onload = showImage;
      loader.onerror = fail;
      loader.src = imageUrl;
      if (loader.complete && loader.naturalWidth > 0) showImage();
    });
    return this.imageLoadPromise;
  }

  buildLightbox() {
    this.lightbox = document.createElement('div');
    this.lightbox.className = 'zentrobad-lightbox';
    this.lightbox.setAttribute('role', 'dialog');
    this.lightbox.setAttribute('aria-modal', 'true');
    this.lightbox.setAttribute('aria-label', 'Produktbilder');
    this.lightbox.setAttribute('hidden', '');
    this.lightbox.innerHTML = `
      <button type="button" class="zentrobad-lightbox__backdrop" aria-label="Galerie schließen" data-lightbox-close></button>
      <div class="zentrobad-lightbox__panel">
        <button type="button" class="zentrobad-lightbox__close" aria-label="Galerie schließen" data-lightbox-close>&times;</button>
        <button type="button" class="zentrobad-lightbox__nav zentrobad-lightbox__nav--prev" aria-label="Vorheriges Bild" data-lightbox-prev>&lsaquo;</button>
        <img class="zentrobad-lightbox__image" alt="" data-lightbox-image>
        <span class="zentrobad-gallery__image-status" role="status" aria-live="polite" aria-atomic="true" data-gallery-image-status hidden></span>
        <button type="button" class="zentrobad-lightbox__nav zentrobad-lightbox__nav--next" aria-label="Nächstes Bild" data-lightbox-next>&rsaquo;</button>
        <div class="zentrobad-lightbox__thumbs" data-lightbox-thumbs></div>
      </div>
    `;

    this.lightboxImage = this.lightbox.querySelector('[data-lightbox-image]');
    this.lightboxThumbs = this.lightbox.querySelector('[data-lightbox-thumbs]');
    this.lightbox.querySelectorAll('[data-lightbox-close]').forEach((button) => {
      button.addEventListener('click', () => this.closeLightbox());
    });
    this.lightbox.querySelector('[data-lightbox-prev]')?.addEventListener('click', () => this.select(this.currentIndex - 1));
    this.lightbox.querySelector('[data-lightbox-next]')?.addEventListener('click', () => this.select(this.currentIndex + 1));
    document.body.append(this.lightbox);
  }

  openLightbox(index) {
    if (!this.currentImages.length || !this.lightbox) return;
    this.isLightboxOpen = true;
    this.lightbox.removeAttribute('hidden');
    document.documentElement.classList.add('zentrobad-lightbox-open');
    this.renderLightboxThumbs();
    this.select(index);
    this.lightbox.querySelector('[data-lightbox-close]')?.focus();
  }

  closeLightbox() {
    if (!this.lightbox) return;
    this.isLightboxOpen = false;
    this.lightbox.setAttribute('hidden', '');
    document.documentElement.classList.remove('zentrobad-lightbox-open');
  }

  renderLightboxThumbs() {
    if (!this.lightboxThumbs) return;
    this.lightboxThumbs.innerHTML = '';
    this.currentImages.forEach((image, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'zentrobad-lightbox__thumb';
      button.setAttribute('aria-label', `Bild ${index + 1} anzeigen`);
      button.dataset.index = String(index);
      button.innerHTML = `<img src="${this.escapeAttribute(image.lowRes)}" alt="${this.escapeAttribute(image.alt)}" loading="lazy">`;
      button.addEventListener('click', () => this.select(index));
      this.lightboxThumbs.append(button);
    });
  }

  updateLightbox() {
    if (!this.lightboxImage || !this.currentImages.length) return;
    this.lightbox?.querySelectorAll('.zentrobad-lightbox__thumb').forEach((thumb) => {
      thumb.toggleAttribute('aria-current', Number(thumb.dataset.index) === this.currentIndex);
    });
  }

  getWishlistProduct() {
    const variantId = Number(this.findSelectedVariantId() || this.data.selectedVariantId || 0);
    const variant = this.data.variants?.find((item) => Number(item.id) === variantId) || this.data.variants?.[0] || {};
    const image = this.currentImages?.[this.currentIndex] || {};

    return {
      id: this.data.productHandle || this.data.productUrl || this.data.productTitle || '',
      title: this.data.productTitle || '',
      handle: this.data.productHandle || '',
      url: this.data.productUrl || window.location.pathname,
      image: image.lowRes || image.highRes || this.data.productImage || variant.featuredImage || '',
      variantId: variant.id || variantId || null,
      variantTitle: variant.title || '',
      addedAt: new Date().toISOString(),
    };
  }

  readWishlist() {
    try {
      const parsed = JSON.parse(window.localStorage.getItem('zentrobadWishlist') || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }

  writeWishlist(items) {
    window.localStorage.setItem('zentrobadWishlist', JSON.stringify(items.slice(0, 100)));
    window.dispatchEvent(new CustomEvent('zentrobad:wishlist-change', { detail: { items } }));
  }

  updateWishlistButton() {
    if (!this.wishlistButton) return;
    const product = this.getWishlistProduct();
    const isActive = this.readWishlist().some((item) => String(item.id) === String(product.id));
    this.wishlistButton.classList.toggle('is-active', isActive);
    this.wishlistButton.setAttribute('aria-pressed', String(isActive));
    this.wishlistButton.setAttribute(
      'aria-label',
      isActive ? 'Von der Merkliste entfernen' : 'Zur Merkliste hinzufügen'
    );
  }

  updateWishlistCounts() {
    const count = this.readWishlist().length;
    document.querySelectorAll('[data-zentrobad-wishlist-count]').forEach((element) => {
      element.hidden = count === 0;
      element.textContent = count ? String(count) : '';
    });
  }

  normalize(value) {
    return String(value || '').trim().toLowerCase();
  }

  toContainImageUrl(url, size = 1600) {
    if (!url) return '';
    return String(url).replace(/\/v1\/fill\/w_\d+,h_\d+,[^/]+\/[^/]+$/i, (match) => {
      const fileName = match.slice(match.lastIndexOf('/') + 1);
      return `/v1/fit/w_${size},h_${size},q_95/${fileName}`;
    });
  }

  colorForValue(value) {
    const text = this.normalize(value);
    if (text.includes('anthrazit')) return '#474747';
    if (text.includes('eiche')) return '#b7834a';
    if (text.includes('weiß') || text.includes('weiss')) return '#f7f5ef';
    if (text.includes('schwarz')) return '#151515';
    if (text.includes('chrom') || text.includes('chrome')) return '#d8d8d5';
    if (text.includes('beton') || text.includes('grau')) return '#8d8982';
    if (text.includes('natur')) return '#b7834a';
    return '';
  }

  escapeAttribute(value) {
    return String(value || '')
      .replaceAll('&', '&amp;')
      .replaceAll('"', '&quot;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;');
  }
}

if (!customElements.get('zentrobad-product-gallery')) {
  customElements.define('zentrobad-product-gallery', ZentrobadProductGallery);
}
